export type Country =
  | "default"
  | "DE"
  | "NL"
  | "BE"
  | "FR"
  | "FI"
  | "CZ"
  | "SK"
  | "CH"
  | "HU"
  | "IE"
  | "IT"
  | "ES"
  | "GR"
  | "DK"
  | "RO"
  | "TR"
  | "UK"
  | "AT"
  | "NO"
  | "PL"
  | "PT"
  | "SE";

export type SelectorType = "CSS" | "RegEx" | "XPath" | "Path" | "MainSelector";

// Nouveau type pour les sélecteurs avec version de parser
export interface SelectorWithVersion {
  country: Country | "default";
  selectors: Selectors;
  version: string; // parserVersion retourné par l'API
}

export interface LodashOption {
  value: string; // Nom de la fonction Lodash
  label: string; // Label affiché dans le sélecteur
  description: string; // Description pour le tooltip
  id?: string; // Ajout de la propriété id optionnelle
}

export interface LodashFunction {
  id: string; // Index de la fonction Lodash dans la liste
  value: string; // Nom de la fonction Lodash, par exemple 'get'
  args?: string[]; // Arguments pour la fonction
}

export interface Selector {
  type: SelectorType;
  value: string;
  prefix?: string;
  suffix?: string;
  lodashFns?: LodashFunction[]; // Nouveau champ pour spécifier les fonctions Lodash
}

export type Selectors = Record<keyof CarDetails, Selector>;

export interface CarDetails {
  /** Marque du véhicule */
  make?: string;

  /** Modèle du véhicule */
  model?: string;

  /** Titre de l'annonce */
  name?: string;

  /** Année de mise en circulation du véhicule */
  year?: string;

  /** Kilométrage du véhicule, valeur sans unité. Parsing fait par le crawl (traitement de la lib). */
  mileage?: string;

  /** Prix formaté (sans virgule, sans séparateur et sans devise). Parsing fait par le crawl (traitement de la lib). */
  price?: string;

  /** Énergie du véhicule */
  fuel?: string;

  /** Couleur du véhicule */
  color?: string;

  /** Date de publication de l'annonce telle que présente dans l'annonce. */
  listingDate?: string;

  /** Date de mise à jour de l'annonce telle que présente dans l'annonce. */
  updateDate?: string;

  /** Mois de mise en circulation du véhicule */
  month?: string;

  /** Email de contact du vendeur */
  email?: string;

  /** ID du vendeur */
  sellerId?: string;

  /** Prénom du vendeur */
  contactSurname?: string;

  /** Nom du vendeur */
  contactName?: string;

  /** Options du véhicule */
  options?: string;

  /** Type de boîte du véhicule */
  gears?: string;

  /** Nombre de portes du véhicule */
  doors?: string;

  /** Nombre de places du véhicule */
  seats?: string;

  /** Type de carrosserie du véhicule tel que présent dans l'annonce */
  body?: string;

  /** Nombre de photos du véhicule dans l'annonce */
  photoCount?: string;

  /** Puissance du véhicule */
  power?: string;

  /** Libellé présent dans l'annonce pour indiquer si le véhicule a 4 roues motrices */
  isFourWheelDrive?: string;

  /** Cylindrée du véhicule tel que présente dans l'annonce */
  liter?: string;

  /** Code postal du vendeur tel que présent sur l'annonce */
  zipCode?: string;

  /** Website du vendeur */
  sellerWebsite?: string;

  /** SIRET du vendeur */
  establishmentRegistrationNumber?: string;

  /** VIN du véhicule */
  vin?: string;

  /** Information kilométrage tel qu'affiché avec l'unité */
  rawMileage?: string;

  /** Prix tel que présent dans l'annonce */
  rawPrice?: string;

  /** Texte de description du véhicule présent dans l'annonce */
  description?: string;

  /** Nom du garage vendeur tel que présent dans l'annonce */
  sellerName?: string;

  /** Adresse du vendeur telle que présente dans l'annonce */
  address?: string;

  /** Nom de la ville du vendeur telle que présente dans l'annonce */
  city?: string;

  /** Mini site du vendeur tel que présent dans l'annonce */
  miniSite?: string;

  /** Type du vendeur (professionnel, particulier) tel que présent dans l'annonce */
  type?: string;

  /** Nombre de vitesses de la boîte tel que présent dans l'annonce */
  gearCount?: string;

  /** Volume des cylindres en cc tel que présent dans l'annonce */
  cylinder?: string;

  /** Information brute non clairement définie et qui doit être parsée par le processus */
  rawData?: string;

  /** Catégorie du véhicule telle que présente dans l'annonce (ex : Mini_citadine, petite voiture, voiture compacte, etc.) */
  category?: string;

  /** Année du modèle si présente dans l'annonce */
  modelYear?: string;

  /** Norme Euro du véhicule telle que présente dans l'annonce (ex : Euro 1, Euro 2, ..., Euro 6d) */
  europeanStandard?: string;

  /** État du véhicule tel que présent dans l'annonce */
  vehicleCondition?: string;

  /** Permis nécessaire pour conduire le véhicule tel que présent dans l'annonce */
  driverLicense?: string;

  /** Référence de l'annonce telle que présente dans l'annonce - DOUBLON de original_site */
  adReference?: string;

  /** Puissance fiscale du véhicule telle que présente dans l'annonce */
  fiscalPower?: string;

  /** Capacité de la batterie telle que présente dans l'annonce */
  batteryCapacity?: string;

  /** Autonomie de la batterie telle que présente dans l'annonce */
  batteryLife?: string;

  /** Nombre de photos du véhicule dans l'annonce (doublon) */
  photoCountDuplicate?: string;

  /** Label VO tel que présent dans l'annonce */
  usedCarLabel?: string;

  /** Information Crit'Air telle que présente dans l'annonce */
  pollutionSticker?: string;

  /** Information génération telle que présente dans l'annonce */
  generation?: string;

  /** Nom du partenaire financier proposant le financement du véhicule */
  leasingFinancialPartner?: string;

  /** Type de financement, tel que présent dans l'annonce */
  leasingFundingType?: string;

  /** Mensualité du financement */
  leasingMonthlyPayment?: string;

  /** Kilométrage annuel maximum inclus dans le plan de financement */
  leasingAnnualMileage?: string;

  /** Type de client pour lequel le financement est prévu. Financement pour particulier ou financement pour professionnel */
  customerType?: string;

  /** Liste des prix de leasing */
  leasingPriceList?: string;

  /** Prix d'achat du leasing */
  leasingPurchasePrice?: string;

  /** Prix de fin de leasing */
  leasingEndingPrice?: string;

  /** Dépôt de garantie du leasing */
  leasingDeposit?: string;

  /** Remise sur le leasing */
  leasingDiscount?: string;

  /** Prix financé du leasing */
  leasingFinancedPrice?: string;

  /** Offre de leasing */
  leasingOffer?: string;

  /** Kilométrage mensuel du leasing */
  leasingMonthlyMileage?: string;

  /** Durée minimale du leasing */
  leasingMinimumDuration?: string;

  /** Longueur du véhicule telle que présente dans l'annonce */
  vehicleLength?: string;

  /** Largeur du véhicule telle que présente dans l'annonce */
  vehicleWidth?: string;

  /** Hauteur du véhicule telle que présente dans l'annonce */
  vehicleHeight?: string;

  /** Prix TTC tel que présent dans l'annonce */
  priceInclVat?: string;

  /** Prix HT tel que présent dans l'annonce */
  priceExclVat?: string;

  /** Prix financé tel que présent dans l'annonce */
  financedPrice?: string;

  /** Département tel que présent dans l'annonce */
  department?: string;

  /** Province telle que présente dans l'annonce */
  province?: string;

  /** Région telle que présente dans l'annonce */
  region?: string;

  /** Téléphone de l'annonce tel que présent dans l'annonce */
  phone?: string;

  /** Téléphone du vendeur tel que présent dans l'annonce */
  dealerPhone?: string;

  /** Nombre de vidéos du véhicule dans l'annonce */
  videoCount?: string;

  /** Nombre de panoramas du véhicule dans l'annonce */
  panoramaCount?: string;

  /** Véhicule importé ou non, information telle que présente sur le site web */
  vehicleImported?: string;

  /** Capacité de la batterie telle que présente dans l'annonce */
  batterySoh?: string;

  /** Pays de l'annonce issue du crawl */
  country?: string;

  /** Stand ID de Standvirtual, exemple : 'SV123456' */
  standId?: string;

  /** Couleur de l'intérieur du véhicule telle que présente dans l'annonce */
  interiorColor?: string;

  /** Poids du véhicule tel que présent dans l'annonce */
  vehicleWeight?: string;

  /** Numéro d'immatriculation du véhicule tel que présent dans l'annonce */
  registrationPlate?: string;

  /** Latitude du vendeur telle que présente dans l'annonce */
  latitude?: string;

  /** Longitude du vendeur telle que présente dans l'annonce */
  longitude?: string;

  /** Date de première immatriculation du véhicule telle que présente dans l'annonce */
  firstRegistrationDate?: string;

  /** Indique si le vendeur est un professionnel */
  isPro?: string;

  /** URL de l'annonce telle que présente dans l'annonce */
  urlParsed?: string;

  /** ID de l'annonce telle que présente dans l'annonce */
  idParsed?: string;

  /** TVA récupérable de l'annonce telle que présente dans l'annonce */
  vatRecoverable?: string;
}

export const carDetailsKeys: (keyof CarDetails)[] = [
  "make",
  "model",
  "name",
  "year",
  "mileage",
  "price",
  "fuel",
  "color",
  "listingDate",
  "updateDate",
  "month",
  "email",
  "sellerId",
  "contactSurname",
  "contactName",
  "options",
  "gears",
  "doors",
  "seats",
  "body",
  "photoCount",
  "power",
  "isFourWheelDrive",
  "liter",
  "zipCode",
  "sellerWebsite",
  "establishmentRegistrationNumber",
  "vin",
  "rawMileage",
  "rawPrice",
  "description",
  "sellerName",
  "address",
  "city",
  "miniSite",
  "type",
  "gearCount",
  "cylinder",
  "rawData",
  "category",
  "modelYear",
  "europeanStandard",
  "vehicleCondition",
  "driverLicense",
  "adReference",
  "fiscalPower",
  "batteryCapacity",
  "batteryLife",
  "photoCountDuplicate",
  "usedCarLabel",
  "pollutionSticker",
  "generation",
  "leasingFinancialPartner",
  "leasingFundingType",
  "leasingMonthlyPayment",
  "leasingAnnualMileage",
  "customerType",
  "leasingPriceList",
  "leasingPurchasePrice",
  "leasingEndingPrice",
  "leasingDeposit",
  "leasingDiscount",
  "leasingFinancedPrice",
  "leasingOffer",
  "leasingMonthlyMileage",
  "leasingMinimumDuration",
  "vehicleLength",
  "vehicleWidth",
  "vehicleHeight",
  "priceInclVat",
  "priceExclVat",
  "financedPrice",
  "department",
  "province",
  "region",
  "phone",
  "dealerPhone",
  "videoCount",
  "panoramaCount",
  "vehicleImported",
  "batterySoh",
  "country",
  "standId",
  "interiorColor",
  "vehicleWeight",
  "registrationPlate",
  "latitude",
  "longitude",
  "firstRegistrationDate",
  "isPro",
  "urlParsed",
  "idParsed",
  "vatRecoverable"
];

export const carDetailsFields: {
  name: keyof CarDetails;
  type: string;
  comment: string;
  context: string;
}[] = [
  {
    name: "make",
    type: "string",
    comment: "Marque du véhicule",
    context: "vehicle",
  },
  {
    name: "model",
    type: "string",
    comment: "Modèle du véhicule",
    context: "vehicle",
  },
  {
    name: "name",
    type: "string",
    comment: "Titre de l'annonce",
    context: "listing",
  },
  {
    name: "year",
    type: "string",
    comment: "Année de mise en circulation du véhicule",
    context: "vehicle",
  },
  {
    name: "mileage",
    type: "string",
    comment:
      "Kilométrage du véhicule, valeur sans unité. Parsing fait par le crawl (traitement de la lib).",
    context: "vehicle",
  },
  {
    name: "price",
    type: "string",
    comment:
      "Prix formaté (sans virgule, sans séparateur et sans devise). Parsing fait par le crawl (traitement de la lib).",
    context: "vehicle",
  },
  {
    name: "fuel",
    type: "string",
    comment: "Énergie du véhicule",
    context: "vehicle",
  },
  {
    name: "color",
    type: "string",
    comment: "Couleur du véhicule",
    context: "vehicle",
  },
  {
    name: "listingDate",
    type: "string",
    comment:
      "Date de publication de l'annonce telle que présente dans l'annonce.",
    context: "listing",
  },
  {
    name: "updateDate",
    type: "string",
    comment:
      "Date de mise à jour de l'annonce telle que présente dans l'annonce.",
    context: "listing",
  },
  {
    name: "month",
    type: "string",
    comment: "Mois de mise en circulation du véhicule",
    context: "vehicle",
  },
  {
    name: "email",
    type: "string",
    comment: "Email de contact du vendeur",
    context: "dealer",
  },
  {
    name: "sellerId",
    type: "string",
    comment: "ID du vendeur",
    context: "dealer",
  },
  {
    name: "contactSurname",
    type: "string",
    comment: "Prénom du vendeur",
    context: "dealer",
  },
  {
    name: "contactName",
    type: "string",
    comment: "Nom du vendeur",
    context: "dealer",
  },
  {
    name: "options",
    type: "string",
    comment: "Options du véhicule",
    context: "vehicle",
  },
  {
    name: "gears",
    type: "string",
    comment: "Type de boîte du véhicule",
    context: "vehicle",
  },
  {
    name: "doors",
    type: "string",
    comment: "Nombre de portes du véhicule",
    context: "vehicle",
  },
  {
    name: "seats",
    type: "string",
    comment: "Nombre de places du véhicule",
    context: "vehicle",
  },
  {
    name: "body",
    type: "string",
    comment: "Type de carrosserie du véhicule tel que présent dans l'annonce",
    context: "vehicle",
  },
  {
    name: "photoCount",
    type: "string",
    comment: "Nombre de photos du véhicule dans l'annonce",
    context: "listing",
  },
  {
    name: "power",
    type: "string",
    comment: "Puissance du véhicule",
    context: "vehicle",
  },
  {
    name: "isFourWheelDrive",
    type: "string",
    comment:
      "Libellé présent dans l'annonce pour indiquer si le véhicule a 4 roues motrices",
    context: "vehicle",
  },
  {
    name: "liter",
    type: "string",
    comment: "Cylindrée du véhicule tel que présente dans l'annonce",
    context: "vehicle",
  },
  {
    name: "zipCode",
    type: "string",
    comment: "Code postal du vendeur tel que présent sur l'annonce",
    context: "dealer",
  },
  {
    name: "sellerWebsite",
    type: "string",
    comment: "Website du vendeur",
    context: "dealer",
  },
  {
    name: "establishmentRegistrationNumber",
    type: "string",
    comment: "SIRET du vendeur",
    context: "dealer",
  },
  {
    name: "vin",
    type: "string",
    comment: "VIN du véhicule",
    context: "vehicle",
  },
  {
    name: "rawMileage",
    type: "string",
    comment: "Information kilométrage tel qu'affiché avec l'unité",
    context: "vehicle",
  },
  {
    name: "rawPrice",
    type: "string",
    comment: "Prix tel que présent dans l'annonce",
    context: "vehicle",
  },
  {
    name: "description",
    type: "string",
    comment: "Texte de description du véhicule présent dans l'annonce",
    context: "listing",
  },
  {
    name: "sellerName",
    type: "string",
    comment: "Nom du garage vendeur tel que présent dans l'annonce",
    context: "dealer",
  },
  {
    name: "address",
    type: "string",
    comment: "Adresse du vendeur telle que présente dans l'annonce",
    context: "dealer",
  },
  {
    name: "city",
    type: "string",
    comment: "Nom de la ville du vendeur telle que présente dans l'annonce",
    context: "dealer",
  },
  {
    name: "miniSite",
    type: "string",
    comment: "Mini site du vendeur tel que présent dans l'annonce",
    context: "dealer",
  },
  {
    name: "type",
    type: "string",
    comment:
      "Type du vendeur (professionnel, particulier) tel que présent dans l'annonce",
    context: "dealer",
  },
  {
    name: "gearCount",
    type: "string",
    comment: "Nombre de vitesses de la boîte tel que présent dans l'annonce",
    context: "vehicle",
  },
  {
    name: "cylinder",
    type: "string",
    comment: "Volume des cylindres en cc tel que présent dans l'annonce",
    context: "vehicle",
  },
  {
    name: "rawData",
    type: "string",
    comment:
      "Information brute non clairement définie et qui doit être parsée par le processus",
    context: "listing",
  },
  {
    name: "category",
    type: "string",
    comment:
      "Catégorie du véhicule telle que présente dans l'annonce (ex : Mini_citadine, petite voiture, voiture compacte, etc.)",
    context: "vehicle",
  },
  {
    name: "modelYear",
    type: "string",
    comment: "Année du modèle si présente dans l'annonce",
    context: "vehicle",
  },
  {
    name: "europeanStandard",
    type: "string",
    comment:
      "Norme Euro du véhicule telle que présente dans l'annonce (ex : Euro 1, Euro 2, ..., Euro 6d)",
    context: "vehicle",
  },
  {
    name: "vehicleCondition",
    type: "string",
    comment: "État du véhicule tel que présent dans l'annonce",
    context: "vehicle",
  },
  {
    name: "driverLicense",
    type: "string",
    comment:
      "Permis nécessaire pour conduire le véhicule tel que présent dans l'annonce",
    context: "vehicle",
  },
  {
    name: "adReference",
    type: "string",
    comment:
      "Référence de l'annonce telle que présente dans l'annonce - DOUBLON de original_site",
    context: "listing",
  },
  {
    name: "fiscalPower",
    type: "string",
    comment: "Puissance fiscale du véhicule telle que présente dans l'annonce",
    context: "vehicle",
  },
  {
    name: "batteryCapacity",
    type: "string",
    comment: "Capacité de la batterie telle que présente dans l'annonce",
    context: "vehicle",
  },
  {
    name: "batteryLife",
    type: "string",
    comment: "Autonomie de la batterie telle que présente dans l'annonce",
    context: "vehicle",
  },
  {
    name: "photoCountDuplicate",
    type: "string",
    comment: "Nombre de photos du véhicule dans l'annonce (doublon)",
    context: "listing",
  },
  {
    name: "usedCarLabel",
    type: "string",
    comment: "Label VO tel que présent dans l'annonce",
    context: "vehicle",
  },
  {
    name: "pollutionSticker",
    type: "string",
    comment: "Information Crit'Air telle que présente dans l'annonce",
    context: "vehicle",
  },
  {
    name: "generation",
    type: "string",
    comment: "Information génération telle que présente dans l'annonce",
    context: "vehicle",
  },
  {
    name: "leasingFinancialPartner",
    type: "string",
    comment: "Nom du partenaire financier proposant le financement du véhicule",
    context: "leasing",
  },
  {
    name: "leasingFundingType",
    type: "string",
    comment: "Type de financement, tel que présent dans l'annonce",
    context: "leasing",
  },
  {
    name: "leasingMonthlyPayment",
    type: "string",
    comment: "Mensualité du financement",
    context: "leasing",
  },
  {
    name: "leasingAnnualMileage",
    type: "string",
    comment: "Kilométrage annuel maximum inclus dans le plan de financement",
    context: "leasing",
  },
  {
    name: "customerType",
    type: "string",
    comment:
      "Type de client pour lequel le financement est prévu. Financement pour particulier ou financement pour professionnel",
    context: "listing",
  },
  {
    name: "leasingPriceList",
    type: "string",
    comment: "Liste des prix de leasing",
    context: "leasing",
  },
  {
    name: "leasingPurchasePrice",
    type: "string",
    comment: "Prix d'achat du leasing",
    context: "leasing",
  },
  {
    name: "leasingEndingPrice",
    type: "string",
    comment: "Prix de fin de leasing",
    context: "leasing",
  },
  {
    name: "leasingDeposit",
    type: "string",
    comment: "Dépôt de garantie du leasing",
    context: "leasing",
  },
  {
    name: "leasingDiscount",
    type: "string",
    comment: "Remise sur le leasing",
    context: "leasing",
  },
  {
    name: "leasingFinancedPrice",
    type: "string",
    comment: "Prix financé du leasing",
    context: "leasing",
  },
  {
    name: "leasingOffer",
    type: "string",
    comment: "Offre de leasing",
    context: "leasing",
  },
  {
    name: "leasingMonthlyMileage",
    type: "string",
    comment: "Kilométrage mensuel du leasing",
    context: "leasing",
  },
  {
    name: "leasingMinimumDuration",
    type: "string",
    comment: "Durée minimale du leasing",
    context: "leasing",
  },
  {
    name: "vehicleLength",
    type: "string",
    comment: "Longueur du véhicule telle que présente dans l'annonce",
    context: "vehicle",
  },
  {
    name: "vehicleWidth",
    type: "string",
    comment: "Largeur du véhicule telle que présente dans l'annonce",
    context: "vehicle",
  },
  {
    name: "vehicleHeight",
    type: "string",
    comment: "Hauteur du véhicule telle que présente dans l'annonce",
    context: "vehicle",
  },
  {
    name: "priceInclVat",
    type: "string",
    comment: "Prix TTC tel que présent dans l'annonce",
    context: "vehicle",
  },
  {
    name: "priceExclVat",
    type: "string",
    comment: "Prix HT tel que présent dans l'annonce",
    context: "vehicle",
  },
  {
    name: "vatRecoverable",
    type: "string",
    comment: "TVA récupérable de l'annonce telle que présente dans l'annonce",
    context: "vehicle",
  },
  {
    name: "financedPrice",
    type: "string",
    comment: "Prix financé tel que présent dans l'annonce",
    context: "leasing",
  },
  {
    name: "department",
    type: "string",
    comment: "Département tel que présent dans l'annonce",
    context: "dealer",
  },
  {
    name: "province",
    type: "string",
    comment: "Province telle que présente dans l'annonce",
    context: "dealer",
  },
  {
    name: "region",
    type: "string",
    comment: "Région telle que présente dans l'annonce",
    context: "dealer",
  },
  {
    name: "phone",
    type: "string",
    comment: "Téléphone de l'annonce tel que présent dans l'annonce",
    context: "listing",
  },
  {
    name: "dealerPhone",
    type: "string",
    comment: "Téléphone du vendeur tel que présent dans l'annonce",
    context: "dealer",
  },
  {
    name: "videoCount",
    type: "string",
    comment: "Nombre de vidéos du véhicule dans l'annonce",
    context: "listing",
  },
  {
    name: "panoramaCount",
    type: "string",
    comment: "Nombre de panoramas du véhicule dans l'annonce",
    context: "listing",
  },
  {
    name: "vehicleImported",
    type: "string",
    comment:
      "Véhicule importé ou non, information telle que présente sur le site web",
    context: "vehicle",
  },
  {
    name: "batterySoh",
    type: "string",
    comment: "Capacité de la batterie telle que présente dans l'annonce",
    context: "vehicle",
  },
  {
    name: "standId",
    type: "string",
    comment: "Stand ID de Standvirtual, exemple : 'SV123456'",
    context: "dealer",
  },
  {
    name: "interiorColor",
    type: "string",
    comment:
      "Couleur de l'intérieur du véhicule telle que présente dans l'annonce",
    context: "vehicle",
  },
  {
    name: "vehicleWeight",
    type: "string",
    comment: "Poids du véhicule tel que présent dans l'annonce",
    context: "vehicle",
  },
  {
    name: "registrationPlate",
    type: "string",
    comment:
      "Numéro d'immatriculation du véhicule tel que présent dans l'annonce",
    context: "vehicle",
  },
  {
    name: "latitude",
    type: "string",
    comment: "Latitude du vendeur telle que présente dans l'annonce",
    context: "dealer",
  },
  {
    name: "longitude",
    type: "string",
    comment: "Longitude du vendeur telle que présente dans l'annonce",
    context: "dealer",
  },
  {
    name: "firstRegistrationDate",
    type: "string",
    comment:
      "Date de première immatriculation du véhicule telle que présente dans l'annonce",
    context: "vehicle",
  },
  {
    name: "isPro",
    type: "string",
    comment: "Vendeur professionnel",
    context: "dealer",
  },
  {
    name: "urlParsed",
    type: "string",
    comment: "URL de l'annonce telle que présente dans l'annonce",
    context: "listing",
  },
  {
    name: "idParsed",
    type: "string",
    comment: "ID de l'annonce telle que présente dans l'annonce",
    context: "listing",
  },
];

export const emptyJsonSelectors: Selectors = carDetailsKeys.reduce(
  (acc, key) => ({ ...acc, [key]: { type: "Path", value: "" } }),
  {} as Selectors,
);

export const emptyHtmlSelectors: Selectors = carDetailsKeys.reduce(
  (acc, key) => ({ ...acc, [key]: { type: "CSS", value: "" } }),
  {} as Selectors,
);

// Dictionnaire complet des traductions de contexte
// Dictionnaire complet (enrichi) des traductions de contexte
export const contextTranslations: Record<string, string[]> = {
  car: [
    "voiture",
    "auto",
    "automobile",
    "véhicule",
    "vehicle",
    "car",
    "bagnole", // éventuellement familier
  ],
  price: [
    "prix",
    "coût",
    "tarif",
    "cost",
    "price",
    "value",
    "valeur",
    "montant",
    "amount",
  ],
  technical: [
    "technique",
    "technical",
    "specs",
    "spécifications",
    "caractéristiques",
    "features",
    "données techniques",
  ],
  dimension: [
    "dimension",
    "dimensions",
    "taille",
    "size",
    "measurements",
    "mesures",
    "gabarit",
  ],
  performance: [
    "performance",
    "performances",
    "puissance",
    "power",
    "efficacité",
    "efficiency",
    "rendement",
    "capacité",
  ],
  consumption: [
    "consommation",
    "consumption",
    "fuel",
    "carburant",
    "économie",
    "economy",
    "fuel usage",
    "fuel efficiency",
  ],
  engine: [
    "moteur",
    "engine",
    "powertrain",
    "propulsion",
    "motor",
    "engine block",
  ],
  transmission: [
    "transmission",
    "boîte de vitesse",
    "gearbox",
    "boîte",
    "vitesses",
    "gears",
    "drivetrain",
  ],
  exterior: [
    "extérieur",
    "exterior",
    "outside",
    "dehors",
    "carrosserie",
    "body",
    "bodywork",
  ],
  interior: [
    "intérieur",
    "interior",
    "inside",
    "dedans",
    "habitacle",
    "cabin",
    "cockpit",
  ],
  safety: ["sécurité", "safety", "protection", "sûreté", "security"],
  comfort: [
    "confort",
    "comfort",
    "commodité",
    "convenience",
    "amenities",
    "équipements",
    "ergonomie",
  ],
  infotainment: [
    "infodivertissement",
    "infotainment",
    "entertainment",
    "divertissement",
    "média",
    "media",
    "multimedia",
  ],
  identification: [
    "identification",
    "identity",
    "identité",
    "id",
    "reference",
    "référence",
    // "numéro" si besoin, mais c'est parfois plus large
  ],
};

// Dictionnaire complet (enrichi) des traductions de champs
export const fieldTranslations: Record<string, string[]> = {
  make: [
    "marque",
    "constructeur",
    "fabricant",
    "brand",
    "manufacturer",
    "make",
    "builder",
    "OEM",
  ],
  model: ["modèle", "model", "type", "série", "series", "version", "variant"],
  name: [
    "nom",
    "titre",
    "title",
    "name",
    "heading",
    "libellé",
    "intitulé",
    "annonce",
  ],
  year: [
    "année",
    "millésime",
    "year",
    "model year",
    "année-modèle",
    // "an" (trop générique)
  ],
  price: [
    "prix",
    "coût",
    "tarif",
    "cost",
    "price",
    "value",
    "valeur",
    "montant",
  ],
  mileage: [
    "kilométrage",
    "mileage",
    "distance",
    "odometer",
    "compteur",
    "km",
    "kilomètres",
  ],
  fuel: [
    "carburant",
    "fuel",
    "energy",
    "énergie",
    "gas",
    "essence",
    "diesel",
    "fuel type",
  ],
  transmission: [
    "transmission",
    "boîte de vitesse",
    "gearbox",
    "boîte",
    "vitesses",
    "gears",
    "drivetrain",
  ],
  color: ["couleur", "color", "teinte", "shade", "paint", "peinture", "hue"],
  doors: [
    "portes",
    "doors",
    "accès",
    "access",
    "entrées",
    "entries",
    "openings",
    "doors count",
    "nombre de portes",
    "doorCount",
  ],
  seats: ["sièges", "seats", "places", "spots", "capacity", "capacité"],
  power: [
    "puissance",
    "power",
    "chevaux",
    "horsepower",
    "hp",
    "ch",
    "kw",
    "cv",
  ],
  engine: ["moteur", "engine", "bloc", "block", "propulsion", "powertrain"],
  engineSize: [
    "cylindrée",
    "engine size",
    "displacement",
    "cc",
    "litre",
    "liter",
    "cubic capacity",
    "volume",
  ],
  acceleration: ["accélération", "acceleration", "0-100", "0-60", "sprint"],
  topSpeed: [
    "vitesse max",
    "top speed",
    "maximum speed",
    "vitesse maximale",
    "vitesse de pointe",
    "vmax",
  ],
  length: ["longueur", "length", "long", "taille", "size"],
  width: ["largeur", "width", "wide", "large"],
  height: ["hauteur", "height", "tall", "haut"],
  weight: ["poids", "weight", "masse", "mass", "kg"],
  consumption: [
    "consommation",
    "consumption",
    "fuel economy",
    "économie",
    "l/100km",
    "mpg",
    "fuel usage",
    "fuel efficiency",
  ],
  emissions: [
    "émissions",
    "emissions",
    "co2",
    "carbon",
    "carbone",
    "pollution",
  ],
  vin: [
    "vin",
    "numéro de châssis",
    "chassis number",
    "vehicle identification number",
    "numéro d'identification",
    "niv", // Numéro d'Identification du Véhicule
  ],
  condition: [
    "état",
    "condition",
    "status",
    "statut",
    "quality",
    "qualité",
    "state",
  ],
  features: [
    "équipements",
    "features",
    "options",
    "accessories",
    "accessoires",
  ],
  description: [
    "description",
    "détails",
    "details",
    "information",
    "informations",
    "specs",
    "spécifications",
  ],
  registration: [
    "immatriculation",
    "registration",
    "plate",
    "plaque",
    "license",
    "licence",
    "carte grise", // plus français
  ],
  firstRegistration: [
    "première immatriculation",
    "first registration",
    "date de mise en circulation",
    "registration date",
    "initial registration",
  ],
  warranty: [
    "garantie",
    "warranty",
    "assurance",
    "insurance",
    "coverage",
    "couverture",
  ],
  owners: [
    "propriétaires",
    "owners",
    "previous owners",
    "anciens propriétaires",
    "history",
    "historique",
  ],
  origin: ["origine", "origin", "provenance", "source", "country", "pays"],
  title: ["titre", "title", "document", "papers", "papiers", "documentation"],
  gears: ["vitesses", "gears", "rapports", "speeds", "ratios", "gear ratio"],
  driveType: [
    "transmission",
    "drive type",
    "traction",
    "propulsion",
    "4x4",
    "awd",
    "fwd",
    "rwd",
    "type de transmission",
  ],
  bodyType: [
    "type de carrosserie",
    "body type",
    "style",
    "form",
    "forme",
    "category",
    "catégorie",
    "shape",
  ],
  interior: [
    "intérieur",
    "interior",
    "inside",
    "cabin",
    "habitacle",
    "cockpit",
  ],
  exteriorColor: [
    "couleur extérieure",
    "exterior color",
    "outside color",
    "paint",
    "peinture",
    "coloris extérieur",
  ],
  interiorColor: [
    "couleur intérieure",
    "interior color",
    "inside color",
    "upholstery color",
    "couleur sellerie",
    "coloris intérieur",
  ],
  upholstery: [
    "sellerie",
    "upholstery",
    "seats material",
    "matériau sièges",
    "fabric",
    "tissu",
    "leather",
    "cuir",
    "seat covering",
  ],
  listingDate: [
    "date de publication",
    "listing date",
    "date d'annonce",
    "publication date",
    "date de mise en ligne",
    "online date",
  ],
  updateDate: [
    "date de mise à jour",
    "update date",
    "last update",
    "dernière mise à jour",
    "modified date",
    "date de modification",
  ],
  month: ["mois", "month", "mois de mise en circulation", "registration month"],
  email: [
    "email",
    "courriel",
    "adresse email",
    "e-mail",
    "mail",
    "adresse électronique",
    "contact email",
  ],
  sellerId: [
    "id vendeur",
    "seller id",
    "identifiant vendeur",
    "dealer id",
    "numéro vendeur",
  ],
  contactSurname: [
    "prénom contact",
    "contact first name",
    "prénom",
    "first name",
    "surname",
  ],
  contactName: [
    "nom contact",
    "contact last name",
    "nom",
    "last name",
    "family name",
  ],
  options: [
    "options",
    "équipements",
    "features",
    "accessories",
    "accessoires",
    "extras",
  ],
  body: [
    "carrosserie",
    "body",
    "body style",
    "type de carrosserie",
    "bodywork",
    "shape",
  ],
  photoCount: [
    "nombre de photos",
    "photo count",
    "pictures",
    "photos",
    "images",
    "number of photos",
  ],
  isFourWheelDrive: [
    "4 roues motrices",
    "four wheel drive",
    "4x4",
    "all wheel drive",
    "awd",
    "4wd",
    "traction intégrale",
  ],
  liter: [
    "litres",
    "liters",
    "cylindrée",
    "engine size",
    "displacement",
    "cubic capacity",
  ],
  zipCode: ["code postal", "zip code", "postal code", "cp", "zip"],
  sellerWebsite: [
    "site web vendeur",
    "seller website",
    "dealer website",
    "site internet",
    "website",
  ],
  establishmentRegistrationNumber: [
    "siret",
    "numéro siret",
    "registration number",
    "numéro d'établissement",
    "business id",
    "company number",
  ],
  rawMileage: [
    "kilométrage brut",
    "raw mileage",
    "kilométrage affiché",
    "displayed mileage",
    "mileage with unit",
  ],
  rawPrice: [
    "prix brut",
    "raw price",
    "prix affiché",
    "displayed price",
    "price with currency",
  ],
  sellerName: [
    "nom vendeur",
    "seller name",
    "dealer name",
    "nom du garage",
    "garage name",
    "concession",
  ],
  address: [
    "adresse",
    "address",
    "location",
    "lieu",
    "street address",
    "adresse postale",
  ],
  city: ["ville", "city", "town", "locality", "localité", "municipality"],
  miniSite: [
    "link dealer",
    "link seller",
    "dedicated page",
    "seller page",
    "dealer website",
    "dealer page",
    "dealer link",
    "seller link",
    "contact link",
    "advertiser link",
    "advertiser page",
    "advertiser website",
  ],
  type: [
    "type",
    "seller type",
    "type de vendeur",
    "professional",
    "private",
    "particulier",
    "professionnel",
  ],
  gearCount: [
    "nombre de vitesses",
    "gear count",
    "number of gears",
    "speeds",
    "vitesses",
    "rapports",
  ],
  cylinder: [
    "cylindrée",
    "cylinder",
    "engine capacity",
    "cc",
    "cubic centimeters",
    "centimètres cubes",
  ],
  rawData: [
    "données brutes",
    "raw data",
    "données non traitées",
    "unprocessed data",
    "source data",
  ],
  category: [
    "catégorie",
    "category",
    "class",
    "classe",
    "segment",
    "type de véhicule",
  ],
  modelYear: [
    "année modèle",
    "model year",
    "millésime",
    "year of model",
    "version year",
  ],
  europeanStandard: [
    "norme européenne",
    "european standard",
    "euro norm",
    "norme euro",
    "emission standard",
    "norme d'émission",
  ],
  vehicleCondition: [
    "état du véhicule",
    "vehicle condition",
    "condition",
    "état",
    "car condition",
  ],
  driverLicense: [
    "permis",
    "driver license",
    "driving license",
    "permis de conduire",
    "license type",
  ],
  adReference: [
    "référence annonce",
    "ad reference",
    "listing reference",
    "reference number",
    "numéro de référence",
  ],
  fiscalPower: [
    "puissance fiscale",
    "fiscal power",
    "tax horsepower",
    "cv fiscaux",
    "chevaux fiscaux",
  ],
  batteryCapacity: [
    "capacité batterie",
    "battery capacity",
    "battery size",
    "taille batterie",
    "kwh",
  ],
  batteryLife: [
    "autonomie batterie",
    "battery life",
    "range",
    "autonomie",
    "electric range",
  ],
  photoCountDuplicate: [
    "nombre de photos dupliqué",
    "duplicate photo count",
    "photo number",
    "image count",
  ],
  usedCarLabel: [
    "label vo",
    "used car label",
    "certification",
    "label occasion",
    "quality label",
  ],
  pollutionSticker: [
    "vignette pollution",
    "pollution sticker",
    "crit'air",
    "environmental badge",
    "emission badge",
  ],
  generation: [
    "génération",
    "generation",
    "model generation",
    "version",
    "series",
  ],
  leasingFinancialPartner: [
    "partenaire financier leasing",
    "leasing financial partner",
    "finance partner",
    "financial institution",
    "bank partner",
  ],
  leasingFundingType: [
    "type de financement leasing",
    "leasing funding type",
    "finance type",
    "funding option",
    "financing method",
  ],
  leasingMonthlyPayment: [
    "mensualité leasing",
    "leasing monthly payment",
    "monthly installment",
    "monthly cost",
    "monthly fee",
  ],
  leasingAnnualMileage: [
    "kilométrage annuel leasing",
    "leasing annual mileage",
    "yearly mileage",
    "annual distance",
    "mileage allowance",
  ],
  customerType: [
    "type de client",
    "customer type",
    "client type",
    "buyer type",
    "target customer",
  ],
  leasingPriceList: [
    "liste prix leasing",
    "leasing price list",
    "finance options",
    "pricing options",
    "leasing rates",
  ],
  leasingPurchasePrice: [
    "prix d'achat leasing",
    "leasing purchase price",
    "buy price",
    "purchase option",
    "final payment",
  ],
  leasingEndingPrice: [
    "prix fin leasing",
    "leasing ending price",
    "residual value",
    "balloon payment",
    "final value",
  ],
  leasingDeposit: [
    "dépôt leasing",
    "leasing deposit",
    "initial payment",
    "down payment",
    "first payment",
  ],
  leasingDiscount: [
    "remise leasing",
    "leasing discount",
    "finance discount",
    "reduction",
    "special offer",
  ],
  leasingFinancedPrice: [
    "prix financé leasing",
    "leasing financed price",
    "amount financed",
    "loan amount",
    "finance sum",
  ],
  leasingOffer: [
    "offre leasing",
    "leasing offer",
    "finance deal",
    "special offer",
    "promotion",
  ],
  leasingMonthlyMileage: [
    "kilométrage mensuel leasing",
    "leasing monthly mileage",
    "monthly distance",
    "monthly allowance",
    "monthly limit",
  ],
  leasingMinimumDuration: [
    "durée minimale leasing",
    "leasing minimum duration",
    "minimum term",
    "contract length",
    "minimum period",
  ],
  vehicleLength: [
    "longueur véhicule",
    "vehicle length",
    "car length",
    "length",
    "long",
  ],
  vehicleWidth: [
    "largeur véhicule",
    "vehicle width",
    "car width",
    "width",
    "wide",
  ],
  vehicleHeight: [
    "hauteur véhicule",
    "vehicle height",
    "car height",
    "height",
    "tall",
  ],
  priceInclVat: [
    "prix ttc",
    "price incl vat",
    "price with tax",
    "tax included",
    "vat included",
  ],
  vatRecoverable: [
    "TVA recouvrable",
    "IVA deducible",
    "IVA detraibile",
  ],
  priceExclVat: [
    "prix ht",
    "price excl vat",
    "price without tax",
    "tax excluded",
    "vat excluded",
  ],
  financedPrice: [
    "prix financé",
    "financed price",
    "finance price",
    "credit price",
    "loan price",
  ],
  department: [
    "département",
    "department",
    "county",
    "administrative area",
    "district",
  ],
  province: ["province", "state", "region", "area", "territory"],
  region: ["région", "region", "area", "zone", "territory"],
  phone: [
    "téléphone",
    "phone",
    "phone number",
    "contact number",
    "tel",
    "mobile",
  ],
  dealerPhone: [
    "téléphone vendeur",
    "dealer phone",
    "seller phone",
    "contact phone",
    "business phone",
  ],
  videoCount: [
    "nombre de vidéos",
    "video count",
    "videos",
    "number of videos",
    "media count",
  ],
  panoramaCount: [
    "nombre de panoramas",
    "panorama count",
    "360 views",
    "virtual tours",
    "panoramic images",
  ],
  vehicleImported: [
    "véhicule importé",
    "vehicle imported",
    "import",
    "foreign car",
    "imported car",
  ],
  batterySoh: [
    "état batterie",
    "battery soh",
    "state of health",
    "battery condition",
    "battery health",
  ],
  country: ["pays", "country", "nation", "location country", "origin country"],
  standId: ["id stand", "stand id", "booth id", "display id", "showcase id"],
  vehicleWeight: [
    "poids véhicule",
    "vehicle weight",
    "car weight",
    "weight",
    "mass",
  ],
  registrationPlate: [
    "plaque d'immatriculation",
    "registration plate",
    "license plate",
    "number plate",
    "reg plate",
  ],
  latitude: [
    "latitude",
    "lat",
    "y coordinate",
    "north coordinate",
    "gps latitude",
  ],
  longitude: [
    "longitude",
    "long",
    "x coordinate",
    "east coordinate",
    "gps longitude",
  ],
  firstRegistrationDate: [
    "date première immatriculation",
    "first registration date",
    "initial registration",
    "first reg date",
    "date of first use",
  ],
  isPro: [
    "est pro",
    "is pro",
    "professional seller",
    "dealer",
    "business seller",
  ],
  urlParsed: ["url analysée", "url parsed", "web address", "link", "page url"],
  idParsed: ["id analysé", "id parsed", "listing id", "ad id", "reference id"],
};
